// `deepClone` sourced from @symbo.ls/utils (a zero-dep, per-file-tree-shaken
// package), NOT the monolithic 'smbls' barrel — same fix as
// workspace/functions/workspaceProjects.js. `create` genuinely needs the
// framework's DOMQL mount pipeline (not swappable), so it's dynamic-imported
// at its one call site in createIframeApp() below — that function only runs
// when an embed/extension/preview iframe actually mounts, never on the shell
// boot path, so a static top-level `import { create } from 'smbls'` here was
// forcing the whole framework bundle into every entry that reaches this file.
import { deepClone } from '@symbo.ls/utils'
import { convertSvgToSymbol } from '@symbo.ls/scratch'
import { embedCompatMethods } from './embedCompatMethods.js'
import { registerEmbedModuleStubs, buildEmbedImportMap } from './embedModuleStubs.js'
import { usesRealmBoundGlobal } from './realmGlobals.js'

const NATIVE_RE = /\{\s*\[native code\]\s*\}/

// EMBED-RERALM-SOURCE-TEXT-GATE-1: an error crossing OUT of the frame realm is
// an instance of the FRAME's `ReferenceError`, never the parent's — so the
// `e instanceof ReferenceError` this used to test was ALWAYS false and the
// fallback below never ran. `.name` is the only realm-agnostic reading.
const isReferenceError = (e) => !!e && (e.name === 'ReferenceError' || e instanceof ReferenceError)

// Iterative walk — recursive form blew the stack on projects whose
// published `data` tree is very deeply nested (deep DOMQL trees + nested
// designSystem branches). WeakSet `seen` still guards against cycles.
//
// THE GATE IS STRUCTURAL, NOT TEXTUAL (EMBED-RERALM-SOURCE-TEXT-GATE-1).
// It used to be `/\b(window|document)\s*\./.test(src)` — a substring guess
// about behaviour that silently skipped `globalThis.`, `self.`, `top.`,
// `location`, `navigator`, `localStorage`, `requestAnimationFrame(cb)`,
// `new Image()` and every alias a function declares for itself. Widening that
// pattern is the same trap one level along; `usesRealmBoundGlobal` instead
// computes the function's FREE IDENTIFIERS and asks whether any of them is
// realm-bound. See `realmGlobals.js` for the set, the scanner, and the
// measured reason "re-realm everything" is wrong (it drops the closure, so a
// closure-using function runs its side effects twice through the fallback).
export function rerealmFunctions(root, frameWindow) {
  if (!root || typeof root !== 'object') return
  const seen = new WeakSet()
  const stack = [root]
  while (stack.length) {
    const node = stack.pop()
    if (!node || typeof node !== 'object' || seen.has(node)) continue
    seen.add(node)
    for (const key in node) {
      if (!Object.prototype.hasOwnProperty.call(node, key)) continue
      const v = node[key]
      if (typeof v === 'function') {
        if (v.__rerealmed) continue
        let src
        try {
          src = Function.prototype.toString.call(v)
        } catch {
          continue
        }
        if (NATIVE_RE.test(src) || !usesRealmBoundGlobal(src)) continue
        let reFn
        try {
          reFn = frameWindow.eval('(' + src + ')')
        } catch {
          continue
        }
        const wrapped = function (...args) {
          try {
            return reFn.apply(this, args)
          } catch (e) {
            if (isReferenceError(e)) return v.apply(this, args)
            throw e
          }
        }
        wrapped.__rerealmed = true
        node[key] = wrapped
      } else if (v && typeof v === 'object') {
        stack.push(v)
      }
    }
  }
}

// Bug #2050: keys that the legacy `updateDesignSystem` path wrote in
// UPPERCASE. The runtime (Icon.js, scratch CONFIG) reads lowercase.
// We mirror so the runtime finds icons that landed at the wrong key.
const _DS_LEGACY_UPPERCASE_KEYS = [
  'icons',
  'svg',
  'color',
  'typography',
  'spacing',
  'theme',
  'fontFamily',
  'cases',
  'media',
  'semanticIcons',
  'animation',
  'gradient',
  'shape',
  'shadow',
  'fontWeight',
  'lineHeight',
  'letterSpacing',
  'transition'
]

function _normalizeDsCaseInPlace(ds) {
  if (!ds || typeof ds !== 'object') return
  for (const cat of _DS_LEGACY_UPPERCASE_KEYS) {
    const upper = cat.toUpperCase()
    const upperVal = ds[upper]
    if (upperVal && typeof upperVal === 'object' && !Array.isArray(upperVal)) {
      if (!ds[cat] || typeof ds[cat] !== 'object') ds[cat] = {}
      Object.assign(ds[cat], upperVal)
    }
  }
}

// Memoize sprite HTML per `designSystem` identity. A canvas page that mounts
// N artboards from the same project would otherwise re-iterate every icon
// key + re-run `convertSvgToSymbol` N times for an identical output — the
// transform is deterministic (key + body → same `<symbol>`), so caching by
// reference is safe. WeakMap keeps the entry alive only as long as the
// designSystem object itself is reachable.
const _spriteHtmlCache = typeof WeakMap !== 'undefined' ? new WeakMap() : null

function _buildSpriteHtml(designSystem) {
  let spriteHtml = ''
  // Bug #2050: read BOTH `icons` (canonical, lowercase) AND `ICONS`
  // (legacy — old `updateDesignSystem` uppercased the category key
  // before saving to MongoDB). Same for `svg` / `SVG`. Without this
  // tolerance, projects that uploaded icons via the broken modal
  // wouldn't see their icons in the preview iframe even after the
  // save path was fixed, because the data already in DB sits at the
  // wrong key.
  for (const lib of [designSystem.icons, designSystem.ICONS, designSystem.svg, designSystem.SVG]) {
    if (!lib) continue
    for (const key in lib) spriteHtml += convertSvgToSymbol(key, lib[key])
  }
  return spriteHtml
}

// The sprite `<symbol>` markup for a design system, memoized per identity.
// Also read by the opaque user-code frame host (userCodeFrame.js), which
// cannot write the frame document and posts this markup instead.
export function spriteHtmlFor(designSystem) {
  if (!designSystem) return ''
  let spriteHtml = _spriteHtmlCache?.get(designSystem)
  if (spriteHtml == null) {
    spriteHtml = _buildSpriteHtml(designSystem)
    _spriteHtmlCache?.set(designSystem, spriteHtml)
  }
  return spriteHtml
}

// Bug #2050 mirror, exported for the user-code frame host (it normalizes the
// copy it posts; the frame never sees the legacy UPPERCASE-only shape).
export function normalizeDesignSystemCase(ds) {
  _normalizeDsCaseInPlace(ds)
  return ds
}

export function injectSprites(doc, designSystem) {
  if (!doc?.body || !designSystem) return
  const spriteHtml = spriteHtmlFor(designSystem)
  if (spriteHtml) {
    doc.body.insertAdjacentHTML(
      'afterbegin',
      `<svg aria-hidden="true" width="0" height="0" style="position:absolute" id="svgSprite">${spriteHtml}</svg>`
    )
  }
}

// Build the embed skeleton into `d` with DOM APIs — NO document.open().
// The importmap must be inserted via createElement to register (innerHTML-
// injected scripts are inert); style/meta go the same way for symmetry.
// Exported (not a `resetIframeDocument`-local closure) so createIframeApp's
// own late-commit recovery checkpoint — right before `create()`, see
// CANVAS-ROUTEMODE-URL-1 below — can rebuild into a document that committed
// AFTER `resetIframeDocument` already returned, using the exact same
// import-map-first skeleton instead of a partial ad-hoc one.
export function buildEmbedSkeletonInto(d) {
  const head = d.head || d.documentElement.appendChild(d.createElement('head'))
  if (!d.body) d.documentElement.appendChild(d.createElement('body'))
  head.textContent = ''
  const im = buildEmbedImportMap().match(/^<script type="importmap">([\s\S]*)<\/script>$/)
  if (im) {
    const s = d.createElement('script')
    s.type = 'importmap'
    s.textContent = im[1]
    head.appendChild(s)
  }
  const meta = d.createElement('meta')
  meta.setAttribute('name', 'color-scheme')
  meta.setAttribute('content', 'light dark')
  head.appendChild(meta)
  const style = d.createElement('style')
  style.textContent =
    '*{margin:0;padding:0;box-sizing:border-box}html,body{width:100%;height:100%;overflow:auto}'
  head.appendChild(style)
  d.body.textContent = ''
}

export async function resetIframeDocument(iframeNode) {
  // A freshly-mounted iframe (no explicit `src` OR still on about:blank)
  // already has its contentDocument ready and `readyState: 'complete'`.
  // Listening for a `load` event after that point hangs forever —
  // canvas-side tile frames hit this because DOMQL creates the <iframe>
  // synchronously before our handler attaches. Only do the reload dance
  // when the iframe is currently pointed somewhere else.
  const currentSrc = iframeNode.getAttribute('src') || ''
  const needsReload = currentSrc && currentSrc !== 'about:blank'
  if (needsReload) {
    iframeNode.src = 'about:blank'
    await new Promise((resolve) => {
      iframeNode.addEventListener('load', resolve, { once: true })
    })
    iframeNode.__smblsEmbedImportMapInstalled = false
  }
  let frameWindow = iframeNode.contentWindow
  let doc = iframeNode.contentDocument || frameWindow?.document
  if (!doc) return { frameWindow, doc: null }

  // ── Late initial-commit guard (Chrome ≥150) ────────────────────────────────
  // The iframe's INITIAL about:blank navigation commits asynchronously, and in
  // newer Chrome it can land AFTER a document.write — silently REPLACING the
  // written document with a virgin one. Downstream that produced a permanent
  // ~1Hz re-embed loop on every canvas tile (CanvasItem's load listener sees a
  // marker-less doc → remounts → open/write re-arms the same race → …):
  // live-measured 2026-08-08 on natali/shop, 234 document replacements across
  // 8 tiles in 30s, tiles visibly flickering between the boot spiral and the
  // rendered page. Strategy:
  //   • FIRST embed on a frame: document.write (fast, atomic, importmap-first)
  //     then settle one tick and, if the frame's committed document is NOT the
  //     one we wrote, rebuild into the COMMITTED doc with DOM APIs.
  //   • RE-embeds (__smblsEmbedWroteOnce): skip document.open() entirely and
  //     DOM-build straight into the live document — DOM mutation causes no
  //     navigation, so the remount cycle physically cannot sustain.
  if (iframeNode.__smblsEmbedWroteOnce) {
    try {
      buildEmbedSkeletonInto(doc)
      iframeNode.__smblsEmbedImportMapInstalled = true
      return { frameWindow, doc }
    } catch {
      // fall through to the write path below
    }
  }
  doc.open()
  // The import-map MUST be the first thing in <head> — it has to precede any
  // module import for the browser to honor it. Resolves legacy `await
  // import('prismjs')` / `'fuse.js'` (bare specifiers from pre-registry-first
  // frank serialization) to embed stub modules instead of failing native ESM
  // resolution. See embedModuleStubs.buildEmbedImportMap.
  const importMap = iframeNode.__smblsEmbedImportMapInstalled ? '' : buildEmbedImportMap()
  doc.write(
    `<!DOCTYPE html><html><head>${importMap}<meta name="color-scheme" content="light dark"><style>*{margin:0;padding:0;box-sizing:border-box}html,body{width:100%;height:100%;overflow:auto}</style></head><body></body></html>`
  )
  doc.close()
  iframeNode.__smblsEmbedImportMapInstalled = true
  iframeNode.__smblsEmbedWroteOnce = true

  await new Promise((resolve) => setTimeout(resolve, 30))
  const committed = iframeNode.contentDocument
  if (committed && committed !== doc) {
    try {
      buildEmbedSkeletonInto(committed)
      doc = committed
      frameWindow = iframeNode.contentWindow
    } catch {
      // Fall through with the originally-written doc — the caller's own
      // load-listener retry remains the outer safety net.
    }
  }
  return { frameWindow, doc }
}

// WS-IFRAMEAPP-SPREADS-WHOLE-PROJECT-RECORD-1 — the create() option surface.
//
// `create(App, options)` treats `options` AS THE CONTEXT (smbls
// createDomql.js: `prepareContext(app, ctx)` then `app.context = ctx`), so a
// `{ ...project }` spread published EVERY field of the API's project RECORD as
// a live `el.context.<field>` inside the embed realm. The callers hand over
// what the data route returned — preview's `renderProject` and canvas's
// `embedComponent` pass the record verbatim — so `members`, `payments`,
// `versions`, `integrations`, `rolePermissions`, `seats`, `authoringAccess`,
// and `secrets` / `stripe` / `projectPassword` all crossed into a realm that
// runs the embedded project's OWN re-realmed functions.
//
// This is the allowlist of keys `create()` actually reads: the project payload
// buckets, the framework config, the design-system flags and the plugin
// opt-ins. Derived from the context/options reads across the smbls runtime
// packages, not from memory. Same shape as the design-system app's own
// `PROJECT_CONTEXT_KEYS` (workspace/packages/design-system/functions/
// renderDesignSystem.js), which narrows the identical hand-off.
//
// NOT here, on purpose:
//   • `window` / `document` / `parent` / `themeRoot` / `context` — the embed
//     owns the realm, and assigns all five explicitly below. A project value
//     for any of them would be wrong by construction.
//   • `registry` / `routerOptions` / `cssPropsRegistry` — framework OUTPUTS
//     that `prepareContext` writes onto the context; never inputs.
//   • `metadata` — the record's platform metadata. `options.metadata` in
//     smbls fetchOnCreate.js is read off `editor`, never off the context.
//
// MAINTENANCE: when smbls adds a `create()` option a project payload can
// carry, add it here — `createIframeApp.recordBoundary.test.js` pins both
// halves (nothing extra crosses, and nothing legitimate is dropped).
export const CREATE_CONTEXT_KEYS = [
  // Project payload buckets
  'app',
  'pages',
  'components',
  'functions',
  'methods',
  'snippets',
  'utils',
  'files',
  'assets',
  'state',
  'designSystem',
  'globalScope',
  'cases',
  'dependencies',
  'sharedLibraries',
  'symbolsConfig',
  'js',
  'svg',
  // Framework config
  'router',
  'define',
  'defaultExtends',
  'domqlOptions',
  'plugins',
  'key',
  'packageManager',
  'cdnProvider',
  'cssPrefix',
  'cssEngine',
  'scopeSelector',
  'skipDesignSystemInit',
  'verbose',
  'initOptions',
  'preventInject',
  'isDevelopment',
  'localFile',
  'endpoint',
  'serviceRoute',
  'onError',
  'events',
  'renderEvents',
  'root',
  'config',
  'newConfig',
  'editor',
  // Design-system flags
  'globalTheme',
  'themeStorageKey',
  'useReset',
  'useVariable',
  'useFontImport',
  'useIconSprite',
  'useSvgSprite',
  'useDocumentTheme',
  'useDefaultConfig',
  'useDefaultIcons',
  // Plugin opt-ins (prepareContext auto-registers off these)
  'polyglot',
  'fetch',
  'db',
  'shorthand',
  'analyze',
  'sync',
  'inspect',
  'notifications',
  'windows',
  'dropdown',
  'tooltip',
  'emotion'
]

// Own-key pick, so an absent key stays absent rather than arriving as an
// explicit `undefined` — that is what the `{ ...project }` spread did, and
// `prepareContext` distinguishes the two for several options.
const pickCreateContext = (project) => {
  const picked = {}
  for (const key of CREATE_CONTEXT_KEYS) {
    if (Object.hasOwn(project, key)) picked[key] = project[key]
  }
  return picked
}

// Shared bootstrap for mounting any smbls app inside a preview iframe.
// Handles realm-safe cloning, re-realming of project functions, sprite
// injection, create(), and theme resolution. Returns the create() result.
// EMBED-REALM-FN-FOLD-1 — the ONE generic function fold for an embed realm.
//
// `el.call('name')` resolves through `context.methods` → `context.functions`
// (smbls packages/element/src/methods.js `call`). Until this fold, an embed
// realm's `context.functions` was ONLY the embedded project's own
// `data.functions`, so any function the HOST realm registers at runtime —
// every engine-synthesized workspace-module bridge (`moduleData`,
// `moduleAiChat`, `moduleFileUpload`, the approval-gated action trio, …,
// built in workspaceProjects.js and written into the shell's live
// `ctx.functions`) — was missing inside the frame. The page mounted, every
// `el.call` to such a name returned `undefined`, `rows || []` swallowed it and
// the tile rendered "loaded + empty": 22 of 23 bellforge board tiles black,
// with ONE console warn for the first missed name and silence after
// (`warnUnresolvedCall` is once-per-name).
//
// It had been patched four times by NAMING the missing piece at one more call
// site. That cannot converge: the miss is per-REALM, not per-name, so the next
// function registered on the shell is invisible again. `hostContext` folds the
// host realm's WHOLE live function registry in as the LOWEST-priority layer —
// exactly the shape `embedCompatMethods` already gives methods — so a function
// added to the shell tomorrow reaches every opted-in embed realm with no edit
// here, in embedComponent, or on any page.
//
// Precedence (lowest → highest): host realm → the project's own functions →
// explicit `contextOverrides.functions`. The project always wins over the host,
// so a host name can never shadow a project's own implementation.
//
// OPT-IN PER HOST REALM, never global. Pass `hostContext` only when the frame
// is same-heap chrome that legitimately runs with the host's trust surface —
// canvas board tiles (the workspace shell IS the runtime for a project opened
// in-shell at `/<owner>/<key>`) and the workspace-extension pane, which already
// hands over the same registry by hand (workspace/functions/index.js
// renderExtensionInPane). Preview, marketplace and published-project embeds
// pass nothing and keep byte-identical isolation.
//
// Merged into a FRESH object on every boot, never passed by reference:
// smbls `prepareContext` runs `rehydrate(context, 'functions')`, which
// `Object.assign`s destringified values back onto the bucket it was given — a
// live reference would let an embed boot mutate the shell's own registry.
export async function createIframeApp(
  iframeNode,
  data,
  shell,
  { path = '/', contextOverrides = {}, resetDocument = true, hostContext = null } = {}
) {
  if (!iframeNode || !data) return null

  // Kicked off now so it resolves in parallel with resetIframeDocument +
  // the clone/re-realm/sprite-injection work below (needed only at the
  // `create(shell, …)` call near the end of this function). index.js warms
  // this exact chunk at shell entry (`void import('smbls')`), so by the
  // time any embed surface reaches here it typically resolves instantly
  // from the module cache.
  const smblsPromise = import('smbls')
  // Two early-return guards below can bail BEFORE the real `await
  // smblsPromise` further down — mark the promise handled now (same
  // `.catch(() => {})` idiom as index.js's own entry warm-up) so a bail
  // never produces an unhandled-rejection console warning. The later
  // `await smblsPromise` still throws normally on a genuine failure.
  smblsPromise.catch(() => {})

  let frameWindow, doc
  if (resetDocument) {
    ;({ frameWindow, doc } = await resetIframeDocument(iframeNode))
  } else {
    frameWindow = iframeNode.contentWindow
    doc = iframeNode.contentDocument || frameWindow?.document || null
  }
  if (!frameWindow || !doc) return null

  // CANVAS-ROUTEMODE-URL-1 (2026-08-08): stamp the tile's own path onto the
  // iframe's `history` as EARLY as possible so anything that reads it before
  // `create()` sees a sane value, but this alone is not sufficient — see the
  // second application right before `create()` below.
  const applyPath = (win) => {
    try {
      win.history.replaceState(null, '', path)
      return true
    } catch {
      return false
    }
  }
  applyPath(frameWindow)

  const hasRealFunctions = typeof data.app?.onRender === 'function'
  const project = hasRealFunctions ? data : deepClone(data, { window: frameWindow })
  // smbls's deepClone skips dunder keys (__resolved, __adapterAttached, …).
  // Without this re-attach, the pre-resolved Supabase adapter we put on
  // `data.fetch.__resolved` / `data.db.__resolved` in workspace
  // shared/functions/attachDbAdapters.js gets dropped here, and the in-
  // iframe resolveAdapter falls through to a fresh `setup()` cross-realm
  // (which silently produces an empty `{}` shape on the published bundle's
  // race path — visible as the "Loading venues…" stall before this fix).
  if (project !== data) {
    if (data.fetch?.__resolved && project.fetch) {
      project.fetch.__resolved = data.fetch.__resolved
    }
    if (data.db?.__resolved && project.db) {
      project.db.__resolved = data.db.__resolved
    }
  }
  rerealmFunctions(project, frameWindow)

  // Resolve bare npm specifiers the legacy platform pages import at runtime
  // (prismjs / fuse.js) to embed no-op stubs via the __SMBLS_PKGS__ registry,
  // so frank's `await import('prismjs')` preamble resolves registry-first
  // instead of hitting native ESM resolution → "Failed to resolve module
  // specifier" (console-spam ticket: smbls.md "Embed runtime parity"). Both
  // realms: rerealmed methods run in the iframe, plain methods in the parent.
  registerEmbedModuleStubs(frameWindow, typeof globalThis !== 'undefined' ? globalThis : null)

  // Bug #2050: mirror legacy UPPERCASE category keys (`ICONS`, `SVG`,
  // `COLOR`, …) onto their lowercase twins so the runtime Icon
  // component / scratch CONFIG find user-uploaded icons even when
  // they were saved by the pre-fix `updateDesignSystem` path. New
  // writes go to lowercase; this only matters for legacy data.
  _normalizeDsCaseInPlace(project.designSystem)

  injectSprites(doc, project.designSystem)

  // CANVAS-ROUTEMODE-URL-1 (2026-08-08): the clone/rerealm/sprite work above
  // is enough real wall-clock time for the iframe's own initial about:blank
  // navigation to commit LATE (the same Chrome≥150 race resetIframeDocument
  // guards against right after document.write — see its "Late initial-commit
  // guard" comment) — measured live: `frame.contentWindow.location.pathname`
  // reading back as `"blank"` well after a `doc.__smblsEmbedApp` mount had
  // already succeeded (natali/shop, 4 route-mode tiles, 2026-08-08 puppeteer
  // repro). When that lands AFTER `applyPath` above, the wiped history
  // silently falls through to smbls's own `prepareWindow` stub-detection
  // (`context.window.URL` looking unusable on a torn-down realm) — the
  // router then resolves the PARENT canvas page's real address-bar path
  // instead of this tile's page key, warns "no content matched for path
  // <parent path>", and route-driven state (e.g. `activeSection`) never
  // initializes. Re-verify RIGHT BEFORE `create()` — the last checkpoint
  // before the router's first read — and recover exactly like
  // `resetIframeDocument` does: if the committed document changed under us,
  // rebuild into it and re-stamp the path on ITS window before proceeding.
  if (iframeNode.contentDocument && iframeNode.contentDocument !== doc) {
    const committed = iframeNode.contentDocument
    try {
      buildEmbedSkeletonInto(committed)
      iframeNode.__smblsEmbedImportMapInstalled = true
      doc = committed
      frameWindow = iframeNode.contentWindow
      applyPath(frameWindow)
      injectSprites(doc, project.designSystem)
    } catch {
      // Fall through with the pre-existing doc/frameWindow — the caller's
      // own load-listener remount (CanvasItem) remains the outer safety net.
    }
  } else {
    // No document swap — still worth a second stamp: a late commit can also
    // land WITHOUT replacing `contentDocument` identity (some Chrome builds
    // reset `history`/`location` state on the SAME document object during
    // the initial-navigation settle). Cheap to reapply, no-op if unneeded.
    applyPath(frameWindow)
  }

  // Register the legacy-platform compat method-set as the LOWEST-priority
  // layer so embedded `system/platform` pages find `el.getDesignSystem()` /
  // `el.isCanvas()` / … instead of throwing "is not a function" (console-spam
  // ticket: smbls.md "Embed runtime parity"). A project's own method of the
  // same name still wins, and explicit contextOverrides win over both.
  const mergedMethods = {
    ...embedCompatMethods,
    ...(project.methods || {}),
    ...(contextOverrides.methods || {})
  }

  // The functions twin of `mergedMethods` — see the EMBED-REALM-FN-FOLD-1 note
  // on this function for why the host layer exists and why it sits lowest.
  //
  // The host layer is the PROTOTYPE, never a spread copy. A spread snapshots
  // the shell registry at the instant this frame boots, and the shell registry
  // keeps GROWING after that: `workspaceProjects.js` accumulates its capability
  // bridges over several passes (the `mergedDependencies.size` branch mints
  // moduleImport/AssetUrl/Mount/Unmount/Api/Token/Theme only once some app has
  // declared dependencies), and the module loader folds a module's shared
  // libraries in later still. Measured live on the bellforge board: every tile
  // froze at 1976 names while the shell had reached 2162 — 186 missing,
  // including 8 `module*` bridges and the whole `crm*` library. That is the
  // SAME defect one axis along: it was fixed by NAME, then by REALM, and it
  // came back by TIME. `moduleData` survived every earlier check only because
  // it lands on the FIRST pass, before any tile boots.
  //
  // `Object.create(host)` resolves live — `el.call` reads
  // `ctx.functions[name]` (smbls element/methods.js `call`), a plain property
  // read that walks the prototype chain — so a bridge registered on the shell
  // one second from now is callable in a frame that booted a minute ago.
  // Precedence is unchanged and still lowest → highest (host → project →
  // contextOverrides), because own properties shadow the prototype.
  // It also keeps the isolation the spread was written for: everything written
  // into this object lands as an OWN property, so `prepareContext`'s
  // `rehydrate(context, 'functions')` — which `Object.assign`s onto the bucket
  // it is handed — can never write into the shell's own registry. Rehydrate
  // walks own properties only (`deepDestringifyFunctions` guards with
  // `hasOwnProperty`), so it does not flatten the prototype back into a copy.
  const hostFunctions = hostContext && hostContext.functions ? hostContext.functions : null
  const mergedFunctions = Object.assign(hostFunctions ? Object.create(hostFunctions) : {}, {
    ...(project.functions || {}),
    ...(contextOverrides.functions || {})
  })

  // CANVAS-TILE-ROUTER-BOOTS-FIRST-ROUTE-1 — hand the router its initial route
  // as DATA, because this realm has no URL it is allowed to write.
  // `applyPath` above is a best-effort stamp only: `history.replaceState` on an
  // `about:blank` document throws `SecurityError` in Chrome (measured live on
  // the 23-tile bellforge board, 2026-08-21 — every frame reads
  // `location.href === 'about:blank'`, `location.pathname === 'blank'`), and the
  // throw is swallowed by design. smbls's `initRouter` then resolved the initial
  // route from `context.window.location` — its only source — so `normalizePath`
  // turned `'blank'` into `'/'` and `matchRoute('/')` returned the FIRST key of
  // the route table for EVERY tile. All 23 tiles booted `/overview`; the 22
  // whose page was anything else mounted a route their frame was never asked
  // for and painted nothing.
  //
  // `context.router.initialPath` is the channel that carries what the embedder
  // already knows. It does not replace the location read — an app with a real
  // URL is untouched — it only supplies the route where no URL can exist.
  // `router: false` (the single-component branch of embedComponent) stays off:
  // a component tile is not a route and must not gain one here.
  const resolvedRouter =
    contextOverrides.router !== undefined ? contextOverrides.router : project.router
  const routerOption =
    resolvedRouter === false
      ? false
      : {
          ...(resolvedRouter && typeof resolvedRouter === 'object' ? resolvedRouter : {}),
          initialPath: path
        }

  const { create } = await smblsPromise
  const result = await create(shell, {
    // Narrowed to the create() option surface — see CREATE_CONTEXT_KEYS above.
    ...pickCreateContext(project),
    ...contextOverrides,
    // After the spreads: `contextOverrides.router` is already folded into
    // `routerOption` above, so this never drops a caller's `router: false`.
    router: routerOption,
    methods: mergedMethods,
    // `create(App, options)` passes `options` through as the context itself
    // (smbls createDomql.js: `prepareContext(app, ctx)` then `app.context =
    // ctx`), so this TOP-LEVEL key is the one `el.call` reads as
    // `context.functions`. It must come after the `...contextOverrides` spread
    // — that spread would otherwise REPLACE the fold with the overrides alone.
    functions: mergedFunctions,
    // smbls attaches methods to elements via `addMethods(el)` reading
    // `el.context.methods` (packages/element/src/methods.js). A direct
    // `create()` call (this embed path bypasses the full createDomql boot)
    // does NOT lift a TOP-LEVEL `methods` option into `context.methods`, so
    // top-level `methods` alone never reaches embed elements — that's why
    // `el.getDesignSystem()` was undefined on platform tiles (smbls.md →
    // workspace.md "Embed runtime parity" sub-item b). Nest under
    // `context.methods` (the channel addMethods actually reads).
    context: {
      ...(project.context || {}),
      ...(contextOverrides.context || {}),
      methods: mergedMethods,
      functions: mergedFunctions,
      window: frameWindow,
      document: doc
    },
    themeRoot: doc.documentElement,
    document: doc,
    window: frameWindow,
    parent: doc.body
  })

  // `data-theme` is FRAMEWORK-OWNED (RULES.md Rule 50 / DESIGN_SYSTEM.md):
  // `create()` runs `resolveAndApplyTheme(options)` (smbls/src/index.js), which
  // resolves the theme through the full precedence chain — URL `?globalTheme` >
  // `localStorage[themeStorageKey]` > `options.globalTheme` (`data.globalTheme`,
  // spread into the options above), with `'auto'` resolved from
  // `prefers-color-scheme` — and stamps the concrete value onto the `themeRoot`
  // passed above. A manual `matchMedia` + `setAttribute('data-theme', …)`
  // reapply used to live here; it only re-derived a WORSE value (it saw none of
  // the URL/localStorage tiers) and raced the framework's own write — the exact
  // "theme flashes wrong color on first paint" failure the design-system
  // troubleshooting table names. Verified in a live iframe (2026-07-27, after
  // the smbls per-document design-system fix 6eb671bc8 + 5acd20774):
  // `create()` alone sets `data-theme` to `dark` for `globalTheme: 'auto'` on a
  // dark-preference OS and to `light` for `globalTheme: 'light'`, with
  // `--theme-document-background` resolving to the matching value in both.
  //
  // What `create()` does NOT do is publish the resolved root back onto the
  // config, so `changeGlobalTheme` — which writes `data-theme` to
  // `CONFIG.themeRoot` — would have no target (verified: after `create()`,
  // `result.context.designSystem.themeRoot` is unset). Point it at THIS app's
  // own root: the `[data-smbls-app="<key>"]` element when the framework scoped
  // this app as secondary (a re-mount into an already-claimed document — see
  // `isSecondary` in smbls/src/prepare.js), else the iframe's documentElement,
  // which is what a fresh-document mount scopes to (`:root`).
  const appEl = doc.querySelector('[data-smbls-app]') || doc.documentElement

  if (result?.context?.designSystem) {
    result.context.designSystem.themeRoot = appEl
    iframeNode.__projectConfig = result.context.designSystem
  }

  // Ownership marker for the CURRENT document. A freshly inserted iframe can
  // fire its initial about:blank navigation AFTER a successful mount, silently
  // replacing the rendered document with a virgin one — the flags on the
  // iframe NODE (`__projectConfig`, importmap latch) survive, so everything
  // upstream believes the tile rendered while its body is empty. Consumers'
  // `load` listeners (canvas CanvasItem) use this marker to tell "our own
  // document.close() load" (marker present → ignore) from "the document was
  // replaced" (marker gone → remount). Lives on the document, not the node,
  // precisely BECAUSE a replacement wipes it.
  try {
    doc.__smblsEmbedApp = true
  } catch {}

  return result
}
